import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { IconComponent, ButtonComponent } from '../../../shared/ui';
import { ScrollLockService } from '../../../shared/service/scroll-lock.service';
import { ReportIssueFormComponent } from '../report-issue-form/report-issue-form.component';
import { CreatedIssue, IssueModule } from '../../models/support.models';

/**
 * The report form as an overlay on the page the user is already on.
 *
 * This is the PRIMARY reporting path. A sidebar-only entry point would navigate the
 * user away from the broken screen — losing the very context (module, URL, what they
 * were looking at) that the report is supposed to capture.
 */
@Component({
  selector: 'app-report-issue-modal',
  standalone: true,
  imports: [CommonModule, IconComponent, ButtonComponent, ReportIssueFormComponent],
  templateUrl: './report-issue-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportIssueModalComponent {
  private readonly scrollLock = inject(ScrollLockService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly isOpen = input<boolean>(false);
  readonly initialModule = input<IssueModule | null>(null);

  readonly closed = output<void>();

  /** Set on success so the modal swaps the form for the confirmation panel. */
  readonly created = signal<CreatedIssue | null>(null);

  private readonly form = viewChild(ReportIssueFormComponent);

  readonly isBusy = computed(() => this.form()?.busy() ?? false);

  /**
   * Tracks whether WE hold a lock. ScrollLockService is reference-counted and each
   * caller must balance its own lock()/unlock() exactly — without this guard the
   * effect's initial run (isOpen === false) would call an unmatched unlock(), and a
   * destroy-while-open would strand a lock and leave the page permanently unscrollable.
   */
  private locked = false;

  constructor() {
    effect(() => {
      if (this.isOpen()) this.acquireLock();
      else this.releaseLock();
    });

    this.destroyRef.onDestroy(() => this.releaseLock());
  }

  private acquireLock(): void {
    if (this.locked) return;
    this.scrollLock.lock();
    this.locked = true;
  }

  private releaseLock(): void {
    if (!this.locked) return;
    this.scrollLock.unlock();
    this.locked = false;
  }

  onSubmitted(issue: CreatedIssue): void {
    this.created.set(issue);
  }

  close(): void {
    this.created.set(null);
    this.closed.emit();
  }

  viewIssue(): void {
    const id = this.created()?._id;
    this.close();
    if (id) this.router.navigate(['/app/support/issues', id]);
  }

  onBackdropClick(event: MouseEvent): void {
    if (this.isBusy()) return;
    if (event.target === event.currentTarget) this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isBusy()) return;
    if (this.isOpen()) this.close();
  }
}
